import { websiteSupabase } from "@/integrations/supabase/website";
import { BILLING_FN_PATH, type BillingAction } from "@/lib/billing-contract";
import { USAGE_FN_PATH } from "@/lib/usage-contract";
import { AGENTS_FN_PATH } from "@/lib/agents-contract";
import { parseAgentsResponse, type AgentsRoute } from "@/lib/agents-response";

export { BILLING_ACTIONS, BILLING_FN_PATH, type BillingAction } from "@/lib/billing-contract";

// The console talks to two service-role-backed Edge Functions. Both are called
// with the admin's WEBSITE session token (the website function validates it
// natively; the shield function validates it cross-project). The browser never
// holds a service-role key.
const WEBSITE_FN = `${import.meta.env.VITE_WEBSITE_SUPABASE_URL}/functions/v1/admin-website`;
const INVITE_FN = `${import.meta.env.VITE_WEBSITE_SUPABASE_URL}/functions/v1/admin-invite`;
const SEO_FN = `${import.meta.env.VITE_WEBSITE_SUPABASE_URL}/functions/v1/admin-seo`;
const REVIEW_FN = `${import.meta.env.VITE_WEBSITE_SUPABASE_URL}/functions/v1/admin-review`;
const SHIELD_FN = `${import.meta.env.VITE_SHIELD_SUPABASE_URL}/functions/v1/admin-shield`;
const REVENUE_FN = `${import.meta.env.VITE_SHIELD_SUPABASE_URL}/functions/v1/admin-revenue`;
const ORGS_FN = `${import.meta.env.VITE_SHIELD_SUPABASE_URL}/functions/v1/admin-orgs`;
const ORG_USERS_FN = `${import.meta.env.VITE_SHIELD_SUPABASE_URL}/functions/v1/admin-users`;
const INVITES_FN = `${import.meta.env.VITE_SHIELD_SUPABASE_URL}/functions/v1/admin-invites`;

// Billing write-through API (shield-control#166). Stripe is the source of truth;
// these actions write to Stripe and the webhook mirrors plan/subscription_status
// back into the Shield DB within seconds. Contract: src/contracts/admin-billing-api.json.
const BILLING_FN = `${import.meta.env.VITE_SHIELD_SUPABASE_URL}${BILLING_FN_PATH}`;

// Usage/presence aggregate (qadar-assets usage-telemetry v1.0, #201). Owned +
// deployed by shield-control; single overview op, empty request body.
const USAGE_FN = `${import.meta.env.VITE_SHIELD_SUPABASE_URL}${USAGE_FN_PATH}`;

async function accessToken(): Promise<string> {
  const { data } = await websiteSupabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("Not authenticated");
  return token;
}

async function call<T>(url: string, apikey: string, body: unknown): Promise<T> {
  const token = await accessToken();
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey,
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload?.error ?? `HTTP ${res.status}`);
  return payload as T;
}

export const websiteApi = <T>(body: unknown) => call<T>(WEBSITE_FN, import.meta.env.VITE_WEBSITE_SUPABASE_ANON_KEY, body);
export const inviteApi = <T>(body: unknown) => call<T>(INVITE_FN, import.meta.env.VITE_WEBSITE_SUPABASE_ANON_KEY, body);
export const seoApi = <T>(body: unknown) => call<T>(SEO_FN, import.meta.env.VITE_WEBSITE_SUPABASE_ANON_KEY, body);
export const reviewApi = <T>(body: unknown) => call<T>(REVIEW_FN, import.meta.env.VITE_WEBSITE_SUPABASE_ANON_KEY, body);
export const shieldApi = <T>(body: unknown) => call<T>(SHIELD_FN, import.meta.env.VITE_SHIELD_SUPABASE_ANON_KEY, body);
export const revenueApi = <T>(body: unknown) => call<T>(REVENUE_FN, import.meta.env.VITE_SHIELD_SUPABASE_ANON_KEY, body);
export const orgsApi = <T>(body: unknown) => call<T>(ORGS_FN, import.meta.env.VITE_SHIELD_SUPABASE_ANON_KEY, body);
export const orgUsersApi = <T>(body: unknown) => call<T>(ORG_USERS_FN, import.meta.env.VITE_SHIELD_SUPABASE_ANON_KEY, body);
export const invitesApi = <T>(body: unknown) => call<T>(INVITES_FN, import.meta.env.VITE_SHIELD_SUPABASE_ANON_KEY, body);
export const billingApi = <T>(body: { action: BillingAction; org_id: string } & Record<string, unknown>) =>
  call<T>(BILLING_FN, import.meta.env.VITE_SHIELD_SUPABASE_ANON_KEY, body);
export const usageApi = <T>() => call<T>(USAGE_FN, import.meta.env.VITE_SHIELD_SUPABASE_ANON_KEY, {});

// Firstier agent platform (agents-admin-api v1.0, #340): admin-agents lives in the firstier-agents
// project and is path-routed (GET /overview, POST /kill|/pause|/resume).
const AGENTS_FN = `${import.meta.env.VITE_AGENTS_SUPABASE_URL}${AGENTS_FN_PATH}`;

export async function agentsApi<T>(path: AgentsRoute, body?: { agent_id?: string } & Record<string, unknown>): Promise<T> {
  const token = await accessToken();
  const res = await fetch(`${AGENTS_FN}${path}`, {
    method: body === undefined ? "GET" : "POST",
    headers: {
      ...(body === undefined ? {} : { "Content-Type": "application/json" }),
      apikey: import.meta.env.VITE_AGENTS_SUPABASE_ANON_KEY,
      Authorization: `Bearer ${token}`,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return parseAgentsResponse<T>(res, path, body);
}
